/* global axios */

class ContactCsvExportService {
  /**
   * Get the current account ID from the URL path
   */
  get accountId() {
    const isInsideAccountScopedURLs = window.location.pathname.includes('/app/accounts');
    if (isInsideAccountScopedURLs) {
      return window.location.pathname.split('/')[3];
    }
    return '';
  }

  /**
   * Get contact custom attribute definitions (name, key, type)
   */
  async getContactAttributeDefinitions() {
    return axios.get(`api/v1/accounts/${this.accountId}/custom_attribute_definitions`, {
      params: { attribute_model: 'contact_attribute' },
    });
  }

  /**
   * Get conversation custom attribute definitions
   */
  async getConversationAttributeDefinitions() {
    return axios.get(`api/v1/accounts/${this.accountId}/custom_attribute_definitions`, {
      params: { attribute_model: 'conversation_attribute' },
    });
  }

  /**
   * Get summary of all custom attributes with their unique values
   */
  async getCustomAttributesSummary() {
    return axios.get(`api/v1/accounts/${this.accountId}/contacts/custom_attributes_summary`);
  }

  /**
   * Get the list of agents of the account (used to filter by conversation owner)
   */
  async getAgents() {
    return axios.get(`api/v1/accounts/${this.accountId}/agents`);
  }

  /**
   * Get distinct values for a single attribute (contact or conversation), on-demand
   */
  async fetchAttributeValues({ attributeKey, model, search = '', limit = 50 }) {
    const response = await axios.post(`api/v1/accounts/${this.accountId}/contacts/attribute_values`, {
      attribute_key: attributeKey,
      model,
      search,
      limit,
    });
    return response.data?.values || [];
  }

  /**
   * Get count of contacts that match the given filters
   */
  async getFilteredContactsCount(filters = [], dateRange = null, creationDateRange = null, agentId = null, excludePhoneless = false) {
    const hasDateRange = dateRange && (dateRange.from || dateRange.to);
    const hasCreationDateRange = creationDateRange && (creationDateRange.from || creationDateRange.to);

    if (filters.length === 0 && !hasDateRange && !hasCreationDateRange && !agentId && !excludePhoneless) {
      // Return total contacts count
      const response = await axios.get(`api/v1/accounts/${this.accountId}/contacts`, {
        params: { page: 1 }
      });
      return response.data?.meta?.count ?? 0;
    }

    const response = await axios.post(`api/v1/accounts/${this.accountId}/contacts/filter_count`, {
      filters,
      date_range: dateRange || {},
      creation_date_range: creationDateRange || {},
      agent_id: agentId || undefined,
      exclude_phoneless: excludePhoneless || undefined
    });
    return response.data.count;
  }

  /**
   * Generate CSV with progress tracking for large datasets
   */
  async generateCsv({ filters = [], columnOrder = [], columnHeaders = null, dateRange = null, creationDateRange = null, agentId = null, excludePhoneless = false, onProgress = null }) {
    try {
      // Get total count first
      const totalContacts = await this.getFilteredContactsCount(filters, dateRange, creationDateRange, agentId, excludePhoneless);

      if (totalContacts === 0) {
        throw new Error('No se encontraron contactos con los filtros aplicados');
      }

      // Process contacts in batches for large datasets
      if (totalContacts > 5000) {
        return this.generateLargeCsv({ filters, columnOrder, columnHeaders, dateRange, creationDateRange, agentId, excludePhoneless, totalContacts, onProgress });
      }
      return this.generateSmallCsv({ filters, columnOrder, columnHeaders, dateRange, creationDateRange, agentId, excludePhoneless, totalContacts, onProgress });
    } catch (error) {
      console.error('Error generating CSV:', error);
      throw error;
    }
  }

  /**
   * Generate CSV for small datasets (< 5000 contacts) - instant
   */
  async generateSmallCsv({ filters, columnOrder, columnHeaders, dateRange, creationDateRange, agentId, excludePhoneless, totalContacts, onProgress }) {
    try {
      const response = await axios.post(`api/v1/accounts/${this.accountId}/contacts/instant_export`, {
        filters,
        column_order: columnOrder,
        column_headers: columnHeaders || undefined,
        date_range: dateRange || {},
        creation_date_range: creationDateRange || {},
        agent_id: agentId || undefined,
        exclude_phoneless: excludePhoneless || undefined
      });

      if (onProgress) {
        onProgress({
          processed: totalContacts,
          total: totalContacts,
          percentage: 100
        });
      }

      // Extract CSV data from JSON response
      const csvData = response.data.csv_data;
      
      if (!csvData) {
        throw new Error('No CSV data received from server');
      }
      
      return { csvData, totalContacts };
    } catch (error) {
      console.error('Error in small CSV generation:', error);
      throw error;
    }
  }

  /**
   * Generate CSV for large datasets (≥ 5000 contacts) - with progress
   */
  async generateLargeCsv({ filters, columnOrder, columnHeaders, dateRange, creationDateRange, agentId, excludePhoneless, totalContacts, onProgress }) {
    const BATCH_SIZE = 1000;
    let csvData = '';
    let processedContacts = 0;

    try {
      const totalBatches = Math.ceil(totalContacts / BATCH_SIZE);

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
        const offset = batchIndex * BATCH_SIZE;

        // El backend devuelve las filas ya formateadas como CSV (con header en
        // el primer lote), por lo que solo concatenamos los chunks en orden.
        const response = await axios.post(`api/v1/accounts/${this.accountId}/contacts/export_batch`, {
          filters,
          column_order: columnOrder,
          column_headers: columnHeaders || undefined,
          date_range: dateRange || {},
          creation_date_range: creationDateRange || {},
          agent_id: agentId || undefined,
          exclude_phoneless: excludePhoneless || undefined,
          limit: BATCH_SIZE,
          offset
        });

        csvData += response.data.csv_data || '';
        processedContacts += response.data.count || 0;

        if (onProgress) {
          const percentage = Math.round((processedContacts / totalContacts) * 100);
          onProgress({
            processed: processedContacts,
            total: totalContacts,
            percentage
          });
        }

        // Si un lote no devuelve filas, ya no hay más contactos que procesar
        if (!response.data.count) {
          break;
        }

        // Small delay to prevent overwhelming the server
        if (batchIndex < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      return { csvData, totalContacts };
    } catch (error) {
      console.error('Error in large CSV generation:', error);
      throw error;
    }
  }

  /**
   * Download CSV file
   */
  downloadCsv(csvData, filename = null) {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename || `contactos_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}

export default new ContactCsvExportService();